import { API_BASE_URL } from '@synergy/config';
import { DocumentStatus, DocumentType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { FormikErrors } from 'formik';
import {
    PaloDetailView,
    PaloDocumentPreview,
    PaloHistory,
    PaloOverflowMenu,
    PaloRecordPager,
    PaloStatusPill,
    PaloStep,
    PaloTabs,
    usePaloShortcut,
} from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolSpinner } from '@synergy/components';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { hasErrors } from '@synergy/utils';
import { resetUploads } from '@synergy/data-access';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import urlJoin from 'url-join';

import * as ActivityActions from '../../../../common/actions/activity-actions';
import { DetailStream } from '../../../activity-sidebar/details/detail-stream';
import { FormikGroup } from '../../../../common/components/new-stepper/formik-group';
import { INVOICE_TOTALS_DIRTY, PaloInvoiceTotalsBar } from './palo-invoice-totals-bar';
import { InvoiceDetailProvider } from '../../context/invoice-detail-context';
import { InvoiceSteps } from '../models/invoice-steps';
import { PaloCompleteDocumentDialog } from '../../../../common/components/palo/palo-complete-document-dialog';
import { PaloCompleteInvoiceNumberSequenceDialog } from './palo-complete-invoice-number-sequence-dialog';
import {
    PaloDirtyProvider,
    usePaloScopeDirty,
    usePaloStepDirty,
} from '../../../../common/components/palo/palo-dirty-context';
import { PaloDocumentChain, PaloDocumentChainTabName } from '../../../document-chain/palo-document-chain';
import { PaloDocumentChainProvider } from '../../../document-chain/palo-document-chain-context';
import { PaloDocumentNotices } from '../../../../common/components/palo/palo-document-notices';
import { PaloDocumentTitle } from '../../../../common/components/palo/palo-document-title';
import { PaloInvoiceAttachments } from './palo-invoice-attachments';
import { PaloInvoiceDetailActions } from './palo-invoice-detail-actions';
import { PaloInvoiceDetailOverview } from '../pages/overview/palo/palo-invoice-detail-overview';
import { PaloInvoiceDetailPositions } from '../pages/positions/palo/palo-invoice-detail-positions';
import { PaloRecordDock } from '../../../../common/components/palo/palo-record-dock';
import { PaloSaveAction, paloDocumentPhase } from '../../../detail-header/palo-main-action';
import { PaloSkippedInvoicesDialog } from '../../palo/palo-skipped-invoices-dialog';
import { PaloStatusChangeDialog } from '../../../../common/components/palo/palo-status-change-dialog';
import { Prefix } from '../../../../common/models/prefix';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { ReceiptNoteType } from '../../../../common/models/enums/receipt-note-type';
import {
    Reset,
    cancel,
    complete,
    deleteReceiptAlert,
    saveAll,
    updateReceiptAlert,
} from '../store/invoice-detail-actions';
import { SkippedInvoiceResponse } from '../../invoice-api';
import { fetchActivities, resetActivities } from '../../../../common/actions/activity-actions';
import { fetchAllTaxRates } from '../../../configuration/finance/tax-rates/store/tax-rates-actions';
import { fetchOverview } from '../store/overview/invoice-detail-overview-actions';
import { fetchPositions } from '../store/positions/invoice-detail-positions-actions';
import {
    fetchReceiptInternalNotes,
    resetReceiptInternalNotes,
} from '../../../../common/actions/internal-notes-actions';
import { fetchTransitions, resetTransitions } from '../../../../common/actions/transitions-activity-actions';
import {
    invoiceDetailOverviewDataSelector,
    isCreditSelector,
} from '../store/overview/invoice-detail-overview-selectors';
import { invoiceDetailPositionProductItemSelector } from '../store/positions/invoice-detail-positions-selectors';
import {
    invoiceDetailStepperStateSelector,
    invoiceReceiptAlertSelector,
    isRequestingSelector,
} from '../store/invoice-detail-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloDockCounts } from '../../../../common/components/palo/use-palo-dock-counts';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const PaloInvoiceDetailInner = () => {
    // Per step, not globally: the marker's whole job is to report on the step
    // nobody is looking at.
    const overviewDirty = usePaloStepDirty(InvoiceSteps.Overview);
    const positionsDirty = usePaloStepDirty(InvoiceSteps.Positions);
    const totalsDirty = usePaloScopeDirty(INVOICE_TOTALS_DIRTY);
    const dockCounts = usePaloDockCounts();
    const overviewReadOnly = useAppSelector(invoiceDetailStepperStateSelector).readonlySteps.includes(
        InvoiceSteps.Overview
    );
    const dispatch = useThunkDispatch();
    const isRequesting = useAppSelector(isRequestingSelector);
    const [activeStep, setActiveStep] = useState(InvoiceSteps.Overview);
    const navigate = useNavigate();
    const idParamString = useParams<{ id: string }>();
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const idParam = +idParamString.id!;
    const invoiceDetail = useAppSelector(invoiceDetailOverviewDataSelector);
    const invoiceProductItems = useAppSelector(invoiceDetailPositionProductItemSelector);
    const isCredit = useAppSelector(isCreditSelector);
    const receiptAlert = useAppSelector(invoiceReceiptAlertSelector);
    // Counts the times C asked for the remark field, so the stream can act on
    // it once it is mounted.
    const { id: overviewInvoiceId } = invoiceDetail.values;
    /**
     * An existing invoice whose data has not arrived yet. Between the first
     * render and the request going out no flag is set, and the mask stood
     * there empty with an active "Speichern" (#2206, R-134, Kosmetik).
     */
    const notLoadedYet = !Number.isNaN(idParam) && overviewInvoiceId !== idParam;
    const pdfLink = useMemo(() => urlJoin(API_BASE_URL, `/invoices/${idParam}/pdf-invoice`), [idParam]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [showFinishWarning, setShowFinishWarning] = useState(false);
    const [showFinishChangeInvoiceNumberSequenceWarning, setShowFinishChangeInvoiceNumberSequenceWarning] =
        useState(false);
    const [showCancelWarning, setShowCancelWarning] = useState(false);
    // What the server refused to complete, if it refused: shown in the same
    // dialog the batch uses, because a snackbar is gone before the reason has
    // been read (#2210, R-116).
    const [skipped, setSkipped] = useState<SkippedInvoiceResponse | undefined>(undefined);
    const [positionsDetails, setPositionsDetails] = useState({
        netShippingCosts: 0,
        netSurcharge: 0,
        isTaxFree: false,
    });

    useEffect(() => {
        if (overviewInvoiceId && !idParam) {
            navigate(`/invoices/${overviewInvoiceId}`, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overviewInvoiceId, navigate]);

    useEffect(() => {
        dispatch(ActivityActions.setDocumentType(DocumentType.Invoice));

        // A new invoice needs the counter accounts as much as a saved one: the
        // field is mandatory on every position, and fetching them only for a
        // saved invoice left the list empty on /invoices/new (#2210, R-01).
        dispatch(fetchAllTaxRates());

        if (idParam) {
            dispatch(fetchActivities(DocumentType.Invoice, idParam));
            dispatch(fetchTransitions(DocumentType.Invoice, idParam));
            dispatch(fetchReceiptInternalNotes(ReceiptNoteType.Invoices, idParam));
            dispatch(fetchOverview(idParam)).then(() => dispatch(fetchPositions()));
        }
    }, [dispatch, idParam]);

    useEffect(() => {
        return () => {
            dispatch(Reset());
            dispatch(resetReceiptInternalNotes());
            dispatch(resetActivities());
            dispatch(resetTransitions());
            dispatch(resetUploads());
        };
    }, [dispatch]);

    const ref = useRef<{
        submitAll: () => void;
        submit: () => Promise<FormikErrors<unknown>>;
        validate: () => Promise<FormikErrors<unknown>>;
    }>();

    const handleSave = async (): Promise<boolean> => {
        await ref.current?.submit();

        const success = await dispatch(saveAll(positionsDetails));

        if (success && idParam) {
            dispatch(fetchActivities(DocumentType.Invoice, idParam));
            dispatch(fetchReceiptInternalNotes(ReceiptNoteType.Invoices, idParam));
        }

        return success;
    };

    const handleContextChange = (netShippingCosts: number, netSurcharge: number, isTaxFree: boolean): void => {
        setPositionsDetails({ netShippingCosts, netSurcharge, isTaxFree });
    };

    const handlePdfOpen = async () => {
        if (DocumentStatus.Created === invoiceDetail.values.status) {
            const success = await handleSave();

            if (!(success && idParam)) {
                return;
            }
        }

        setPreviewOpen(true);
    };

    const handleStepChange = async (step: number, save = false): Promise<void> => {
        const errors = await ref.current?.submit();

        // Always allow backward stepping
        if (step < activeStep) {
            setActiveStep(step);
            return;
        }

        // Disallow next step if there are currently any errors
        if (errors && hasErrors(errors)) {
            return;
        }

        if (save && !(await dispatch(saveAll(positionsDetails)))) {
            return;
        }
        if (save && idParam) {
            dispatch(fetchActivities(DocumentType.Invoice, idParam));
        }
        setActiveStep(step);
    };

    // Saving and going back, which every other mask gets from PaloDetailHeader.
    // The invoice draws its own chrome on PaloDetailView, so it has to say so
    // itself — without this the two keys that matter most were missing here and
    // nowhere else.
    usePaloShortcut({
        combo: 's',
        meta: true,
        group: 'Beleg',
        label: 'Speichern',
        handler: () => {
            void handleSave();
        },
    });
    // C used to have to bring the history up first, because it lived in a tab
    // that was not on show. It stands at the foot of the mask now and is always
    // mounted, so it registers the key itself and this stand-in can go.
    usePaloShortcut({
        combo: 'Escape',
        group: 'Beleg',
        label: 'Zurück zur Liste',
        handler: () => {
            // A dialog answers Esc itself; leaving as well would close it and
            // walk out of the mask in one keystroke.
            if (document.querySelector('.MuiModal-root, .MuiDialog-root')) {
                return;
            }

            navigate(-1);
        },
    });

    const handleStepAction = async (changeInvoiceNumberSequence: boolean): Promise<void> => {
        const completed = invoiceDetail.values.status === DocumentStatus.Completed;

        // Cancelling a completed invoice saved it first with nothing to save,
        // and "Gespeichert" came up four times before the question.
        if (!completed || overviewDirty || positionsDirty || totalsDirty) {
            const success = await handleSave();
            if (!success) {
                return;
            }
        }

        if (!completed) {
            changeInvoiceNumberSequence
                ? setShowFinishChangeInvoiceNumberSequenceWarning(true)
                : setShowFinishWarning(true);
            return;
        }

        setShowCancelWarning(true);
    };

    const handleFinishInvoiceConfirmed = async (): Promise<void> => {
        if (invoiceDetail.values.status !== DocumentStatus.Completed) {
            setShowFinishWarning(false);
            setSkipped(await dispatch(complete()));

            return;
        }
    };

    const handleFinishInvoiceChangeInvoiceSequenceNumberConfirmed = async (
        prefix: Prefix | undefined
    ): Promise<void> => {
        if (invoiceDetail.values.status !== DocumentStatus.Completed) {
            setShowFinishChangeInvoiceNumberSequenceWarning(false);
            setSkipped(await dispatch(complete({ invoiceNumberSequenceHistoryId: prefix?.id })));

            return;
        }
    };

    /**
     * Head and positions are one tab, not two steps (#2208). An invoice is
     * written from top to bottom: whom it is for, then what is on it. Splitting
     * that in two meant saving in between before the first article could be
     * entered.
     */
    const steps: PaloStep[] = [
        {
            label: <FormattedMessage id="app.invoices.detail.step.overview" />,
            state: activeStep === InvoiceSteps.Overview ? 'now' : 'done',
            dirty: overviewDirty || positionsDirty || totalsDirty,
            onClick: () => handleStepChange(InvoiceSteps.Overview),
        },
        // Which documents an invoice belongs to can only be answered once it
        // is one. ERPNext leaves the same tab out until the first save.
        ...(idParam
            ? ([
                  {
                      label: <PaloDocumentChainTabName />,
                      state: activeStep === InvoiceSteps.Documents ? 'now' : 'todo',
                      // Parks what the open step holds, then shows the chain. It does not
                      // go through handleStepChange: that refuses to move on while the
                      // form has errors, and a tab that only reads must not be locked
                      // away behind a field someone still has to fill in.
                      onClick: async () => {
                          await ref.current?.submit();
                          setActiveStep(InvoiceSteps.Documents);
                      },
                  },
              ] as PaloStep[])
            : []),
    ];

    const dock = (
        <PaloRecordDock
            count={dockCounts.attachments}
            attachments={<PaloInvoiceAttachments recipientReadOnly={overviewReadOnly} />}
        />
    );

    /**
     * Only once the invoice exists. A document that has never been saved has
     * nothing to look back on, and the stream would ask the server for the
     * history of an id that is not a number (#2208).
     */
    const phase = paloDocumentPhase(invoiceDetail.values.status, { saved: !!invoiceDetail.values.id });

    const history = idParam ? (
        <PaloHistory>
            <DetailStream documentType={DocumentType.Invoice} documentId={idParam} isCredit={isCredit} />
        </PaloHistory>
    ) : undefined;

    return (
        <PoolSpinner enabled={isRequesting || notLoadedYet} sx={{ display: 'block', height: '100%' }}>
            <PaloDocumentTitle
                typeId={isCredit ? 'app.document.credit' : 'app.document.invoice'}
                number={
                    DocumentStatus.Created === invoiceDetail.values.status ? undefined : invoiceDetail.values.number
                }
            />
            <InvoiceDetailProvider onChange={handleContextChange}>
                <PaloDetailView
                    themeContent={false}
                    onBack={() => navigate(ROUTES.invoices.list)}
                    number={
                        DocumentStatus.Created === invoiceDetail.values.status ? (
                            <FormattedMessage id={`app.invoices.detail.${isCredit ? 'creditDraft' : 'invoiceDraft'}`} />
                        ) : (
                            invoiceDetail.values.number ?? ''
                        )
                    }
                    title={<FormattedMessage id={isCredit ? 'app.document.credit' : 'app.document.invoice'} />}
                    status={
                        invoiceDetail.values.status ? (
                            <PaloStatusPill
                                status={invoiceDetail.values.status}
                                label={
                                    <FormattedMessage
                                        id={getDocumentStatusTranslationId(invoiceDetail.values.status)}
                                    />
                                }
                            />
                        ) : undefined
                    }
                    tabs={<PaloTabs steps={steps} />}
                    alert={
                        <PaloDocumentNotices
                            alert={receiptAlert}
                            // The notices of supplier and recipient show on a
                            // draft as well (#2236); its own notice waits for
                            // the first save, the endpoint needs an id.
                            onAlertChange={
                                overviewInvoiceId
                                    ? alert =>
                                          alert ? dispatch(updateReceiptAlert(alert)) : dispatch(deleteReceiptAlert())
                                    : undefined
                            }
                        />
                    }
                    breadcrumbs={[
                        {
                            label: <FormattedMessage id="app.navigation.invoices" />,
                            onClick: () => navigate(ROUTES.invoices.list),
                        },
                    ]}
                    actions={
                        <PaloInvoiceDetailActions
                            activeStep={activeStep}
                            phase={phase}
                            onPdfOpen={handlePdfOpen}
                            onComplete={handleStepAction}
                        />
                    }
                    menu={
                        <PaloOverflowMenu
                            actions={[
                                {
                                    label: <FormattedMessage id="app.common.previewPrint" />,
                                    onClick: handlePdfOpen,
                                    disabled: !idParam,
                                },
                            ]}
                        />
                    }
                    preview={{
                        onOpen: () => void handlePdfOpen(),
                        disabled: !idParam,
                        disabledHint: <FormattedMessage id="app.common.previewAfterSave" />,
                    }}
                    pager={
                        <PaloRecordPager
                            currentId={invoiceDetail.values.id}
                            onOpen={id => navigate(`${ROUTES.invoices.list}/${id}`)}
                        />
                    }
                    primaryAction={<PaloSaveAction phase={phase} comparesToServer={true} onSave={handleSave} />}
                    dock={idParam ? dock : undefined}
                    history={history}
                    totals={<PaloInvoiceTotalsBar />}
                >
                    <PaloNextThemeProvider>
                        <FormikGroup ref={ref} activeStep={activeStep} stepperState={{ isNew: true }}>
                            {activeStep === InvoiceSteps.Overview && (
                                <PaloInvoiceDetailOverview positions={<PaloInvoiceDetailPositions />} />
                            )}
                            {activeStep === InvoiceSteps.Documents && <PaloDocumentChain />}
                        </FormikGroup>
                    </PaloNextThemeProvider>
                </PaloDetailView>

                <PaloStatusChangeDialog
                    open={showCancelWarning}
                    onClose={() => setShowCancelWarning(false)}
                    title={
                        <FormattedMessage
                            id="app.menu.action.cancel"
                            values={{
                                value: (
                                    <FormattedMessage id={isCredit ? 'app.document.credit' : 'app.common.invoice'} />
                                ),
                            }}
                        />
                    }
                    description={
                        <FormattedMessage
                            id={
                                isCredit
                                    ? 'app.invoices.detail.cancelWarning.credit'
                                    : 'app.invoices.detail.cancelWarning'
                            }
                        />
                    }
                    confirmLabel={<FormattedMessage id="app.common.yes" />}
                    destructive={true}
                    documentType={DocumentType.Invoice}
                    documentId={invoiceDetail.values.id}
                    onConfirm={() => {
                        setShowCancelWarning(false);
                        dispatch(cancel());
                    }}
                />

                <PaloCompleteDocumentDialog
                    open={showFinishWarning}
                    onClose={() => setShowFinishWarning(false)}
                    onConfirm={handleFinishInvoiceConfirmed}
                    taxes={invoiceProductItems.map(item => item.tax ?? 0)}
                    invoiceLocation={invoiceDetail.values.invoiceAddress?.country}
                    titleId={isCredit ? 'app.invoices.detail.finish.credit' : 'app.invoices.detail.finish'}
                    contentMessageId={
                        isCredit ? 'app.invoices.detail.finishWarning.credit' : 'app.invoices.detail.finishWarning'
                    }
                    documentType={DocumentType.Invoice}
                    documentId={invoiceDetail.values.id}
                />
                <PaloSkippedInvoicesDialog
                    skipped={skipped ? [skipped] : []}
                    single={true}
                    onClose={() => setSkipped(undefined)}
                />
                <PaloDocumentPreview
                    url={previewOpen ? pdfLink : undefined}
                    title={<FormattedMessage id="app.common.preview" />}
                    openLabel={<FormattedMessage id="app.common.openInNewTab" />}
                    closeLabel={<FormattedMessage id="app.common.close" />}
                    onClose={() => setPreviewOpen(false)}
                />
                <PaloCompleteInvoiceNumberSequenceDialog
                    open={showFinishChangeInvoiceNumberSequenceWarning}
                    onClose={() => setShowFinishChangeInvoiceNumberSequenceWarning(false)}
                    onConfirm={handleFinishInvoiceChangeInvoiceSequenceNumberConfirmed}
                    taxes={invoiceProductItems.map(item => item.tax ?? 0)}
                    invoiceLocation={invoiceDetail.values.invoiceAddress?.country}
                    invoiceId={invoiceDetail.values.id}
                />
            </InvoiceDetailProvider>
        </PoolSpinner>
    );
};

export const PaloInvoiceDetail = () => {
    // A fresh invoice from the server is the one moment at which both steps,
    // including the one that is not mounted, are provably saved.
    const loadedDocument = useAppSelector(state => state.invoiceDetail.overview.baseline);

    return (
        <PaloDirtyProvider loadedDocument={loadedDocument}>
            <PaloDocumentChainProvider documentType={DocumentType.Invoice}>
                <PaloInvoiceDetailInner />
            </PaloDocumentChainProvider>
        </PaloDirtyProvider>
    );
};
